import {
  autoLoginQueryOption,
  useCustomMutation,
} from 'src/hooks/react-query/useCustomMutation.hook'
import { Bridge } from 'src/utils/bridge'
import usePageNav from 'src/hooks/usePageNav.hook'
import { useCookies } from 'react-cookie'
import { DateUtils } from 'src/utils/date'
import { encryptPhoneNumber } from 'src/utils'
import { ERRCODE_SUCCESS } from 'src/service/constant/errcodes'
import { AutoLoginSpace } from 'src/types/api'
import { captureMessage } from '@sentry/react'
import { SENTRY_SEVERITY } from 'src/utils/sentry'

/** ------------------------------------------------------------------
 *  Bridge 요청을 Promise로 감싸서 사용
 *  앱 진입시 즉시 사용되는 경우도 있어서 에러나면 앱 진입이 막힘
 * ------------------------------------------------------------------ */
export const requestBridgeValue = <T extends string>(
  reqFunc: () => void, // 이제 void
  resFunc: (cb: (val: T) => void, err: () => void) => void,
) => {
  return new Promise<T>((resolve, reject) => {
    try {
      reqFunc() // void 실행
      resFunc(
        (result: T) => (!!result ? resolve(result) : reject()),
        () => reject(),
      )
    } catch {
      reject()
    }
  })
}

const useUserInfo = () => {
  const { introPageNav } = usePageNav()
  const [, setCookie] = useCookies(['auth-key', 'u-tel'])

  const { sessionCookieOptions } = DateUtils

  const { mutateAsync: autoLoginMutate } = useCustomMutation<
    AutoLoginSpace.Request,
    AutoLoginSpace.Response
  >(autoLoginQueryOption())

  const failFn = () => {
    if (!window.location.pathname?.includes('splash')) introPageNav()
  }

  /** 쿠키가 사라진 경우
   * 앱에서 authKey, pNumber 복원 및 세션 재생성 **/
  const generateSession = async () => {
    try {
      const pNumber = await requestBridgeValue(Bridge.getPNumber.req, Bridge.getPNumber.res)
      const authKey = await requestBridgeValue(Bridge.getAuthKey.req, Bridge.getAuthKey.res)
      const { Header, Body } = await autoLoginMutate({ AuthKey: authKey })
      if (Header.ErrCode !== ERRCODE_SUCCESS || !Body.AuthKey) {
        failFn()
        return false
      }
      setCookie('auth-key', Body.AuthKey, sessionCookieOptions())
      setCookie('u-tel', encryptPhoneNumber(pNumber), sessionCookieOptions())
      Bridge.handleReqSaveAuthKey(Body.AuthKey)
      return pNumber
    } catch {
      failFn()
      return false
    }
  }

  /** 앱에서 pNumber 복원 **/
  const resavePNumber = async () => {
    try {
      const pNumber = await requestBridgeValue(Bridge.getPNumber.req, Bridge.getPNumber.res)
      setCookie('u-tel', encryptPhoneNumber(pNumber), sessionCookieOptions())
      return true
    } catch {
      captureMessage('[Session] resavePNumber failed', {
        level: SENTRY_SEVERITY.Info,
      })
      failFn()
      return false
    }
  }

  // /** ios 한번 호출된 후 재호출 불가 버그 해결용 재저장 **/
  // useEffect(() => {
  //   if (!!c_tel && ios) {
  //     Bridge.getBiometricAgreeState.req(c_tel)
  //     Bridge.getBiometricAgreeState.res((result) => Bridge.handleReqSignUpLogin(c_tel, result))
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [c_tel])

  return { resavePNumber, generateSession }
}
export default useUserInfo
